import { useState, useEffect } from "react";
import { ref, onValue, off } from "firebase/database";
import { database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import { LuCircleX as IoMdCloseCircleOutline } from "react-icons/lu";
import { useNavigate } from "react-router";
import { ROUTES } from "../App";

const ViewMembers = ({
    isOpen,
    onClose,
    membersPath,
    hostId,
    title = "Members",
}) => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const { currentUser } = useUser();
    const navigate = useNavigate();
    const [imageLoading, setImageLoading] = useState({});

    useEffect(() => {
        if (!isOpen || !membersPath) return;

        setLoading(true);

        // Get all member UIDs from the specified path
        const membersRef = ref(database, membersPath);

        const unsubscribe = onValue(membersRef, async (snapshot) => {
            const membersData = snapshot.val();

            if (!membersData) {
                setMembers([]);
                setLoading(false);
                return;
            }

            const memberUids = Object.keys(membersData).filter(uid => membersData[uid] === true);

            // Clear current members before loading
            setMembers([]);

            // Fetch user details for each member
            for (const uid of memberUids) {
                try {
                    const userRef = ref(database, `Users/${uid}`);
                    onValue(userRef, (userSnapshot) => {
                        const userData = userSnapshot.val();
                        if (userData) {
                            // Check if member already exists in the list to avoid duplicates
                            setMembers(prev => {
                                const existing = prev.find(m => m.uid === uid);
                                if (existing) return prev;

                                return [...prev.filter(m => m.uid !== uid), {
                                    uid,
                                    name: userData.name || userData.email || 'Unknown User',
                                    profilePic: userData.profilePic || 'https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef',
                                    email: userData.email,
                                    isHost: uid === hostId,
                                    role: userData.role,
                                    niche: userData.niche || 'No niche specified',
                                }].sort((a, b) => {
                                    // Sort host first, then by name
                                    if (a.isHost && !b.isHost) return -1;
                                    if (!a.isHost && b.isHost) return 1;
                                    return a.name.localeCompare(b.name);
                                });
                            });
                        }
                    });
                } catch (error) {
                    console.error("Error fetching user data:", error);
                }
            }
            console.log("Members fetched:", members);

            setLoading(false);
        });

        return () => {
            off(membersRef);
            setMembers([]);
        };
    }, [isOpen, membersPath, hostId]);

    const handleClose = () => {
        setMembers([]);
        onClose();
    };

    // 🟢 Simple shimmer skeleton loader component
    const ShimmerLoader = () => (
        <div className="flex flex-col space-y-4 mt-8">
            {[...Array(6)].map((_, i) => (
                <div
                    key={i}
                    className="animate-pulse flex items-center justify-between p-3"
                >
                    <div className="flex items-center space-x-2">
                        <div className="w-12 h-12 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
                        <div>
                            <div className="w-40 h-4 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
                            <div className="w-40 h-4 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-[#23272c] rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-xl font-bold text-black dark:text-white">
                        {title}
                    </h2>
                    <button
                        onClick={handleClose}
                        className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                        <IoMdCloseCircleOutline size={20} className="text-gray-500 dark:text-gray-400" />
                    </button>
                </div>

                {/* Members List */}
                <div className="overflow-y-auto max-h-[60vh]">
                    {loading ? (
                        <ShimmerLoader />
                    ) : members.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            No members found
                        </div>
                    ) : (
                        <div className="p-4 space-y-3">
                            {members.map((member) => {
                                const isLoading = imageLoading[member.uid] !== false; // default true

                                return (
                                    <button
                                        key={member.uid}
                                        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer w-full"
                                        onClick={() => navigate(`${ROUTES.VIEW_PROFILE}/${member.uid}`)}
                                    >
                                        <div className="relative w-12 h-12">
                                            {isLoading && (
                                                <div className="absolute inset-0 rounded-full animate-pulse bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700" />
                                            )}
                                            <img
                                                src={member.profilePic}
                                                alt={member.name}
                                                className={`w-12 h-12 rounded-full object-cover transition-opacity duration-300 ${!isLoading ? 'opacity-100' : 'opacity-0'}`}
                                                onLoad={() => setImageLoading(prev => ({ ...prev, [member.uid]: false }))}
                                                onError={() => setImageLoading(prev => ({ ...prev, [member.uid]: false }))}
                                            />
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center space-x-2">
                                                <p className={`flex items-center gap-2 font-medium ${member?.role === "Host" ? "dark:glow-text-dark glow-text-light" : "text-black dark:text-white"} truncate`}>
                                                    {member.name}
                                                </p>
                                                {member.isHost && (
                                                    <span className="px-2 py-1 bg-primary text-white text-xs rounded-full">Host</span>
                                                )}
                                                {member.uid === currentUser?.uid && (
                                                    <span className="px-2 py-1 bg-gray-500 text-white text-xs rounded-full">You</span>
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate text-left">{member.niche}</p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                        {members.length} {members.length === 1 ? 'member' : 'members'}
                    </div>
                </div>
            </div>
        </div >
    );
};

export default ViewMembers;